/**
 * Simple WebM parser to extract Opus frames from MediaRecorder chunks.
 * Supports SimpleBlock (0xA3) and Block (0xA1) elements inside Clusters.
 */
export class WebMParser {
  private buffer: Uint8Array = new Uint8Array(0);

  /**
   * Add WebM chunk and return extracted Opus frames.
   */
  addChunk(data: Uint8Array): Uint8Array[] {
    const merged = new Uint8Array(this.buffer.length + data.length);
    merged.set(this.buffer);
    merged.set(data, this.buffer.length);
    this.buffer = merged;

    const frames: Uint8Array[] = [];
    let offset = 0;
    let extractedAny = false;

    while (offset < this.buffer.length - 4) {
      const marker = this.buffer[offset];

      if (marker === 0xa3) {
        const result = this.extractFromSimpleBlock(offset);
        if (result.frame && result.frame.length > 0) {
          frames.push(result.frame);
          offset = result.nextOffset;
          extractedAny = true;
          continue;
        }
      }

      if (marker === 0xa1) {
        const result = this.extractFromBlock(offset);
        if (result.frame && result.frame.length > 0) {
          frames.push(result.frame);
          offset = result.nextOffset;
          extractedAny = true;
          continue;
        }
      }

      offset++;
    }

    if (extractedAny && offset > 0) {
      const keepSize = Math.min(2048, this.buffer.length - offset);
      if (keepSize > 0 && offset < this.buffer.length) {
        this.buffer = this.buffer.slice(this.buffer.length - keepSize);
      } else {
        this.buffer = new Uint8Array(0);
      }
    }

    return frames;
  }

  /**
   * Extract Opus frame from SimpleBlock
   */
  private extractFromSimpleBlock(offset: number): {
    frame: Uint8Array | null;
    nextOffset: number;
  } {
    if (this.buffer.length - offset < 5) {
      return { frame: null, nextOffset: offset };
    }

    if (this.buffer[offset] !== 0xa3) {
      return { frame: null, nextOffset: offset };
    }

    const startOffset = offset;
    offset++; // Skip 0xA3

    const sizeResult = this.readVint(offset);
    if (sizeResult.value === -1 || sizeResult.value < 1) {
      return { frame: null, nextOffset: startOffset + 1 };
    }
    offset += sizeResult.bytesRead;

    const blockSize = sizeResult.value;
    if (this.buffer.length - offset < blockSize) {
      return { frame: null, nextOffset: startOffset };
    }

    const trackResult = this.readVint(offset);
    if (trackResult.value === -1) {
      return { frame: null, nextOffset: startOffset + 1 };
    }
    offset += trackResult.bytesRead;

    if (this.buffer.length - offset < 2) {
      return { frame: null, nextOffset: startOffset };
    }
    offset += 2; // timecode

    if (this.buffer.length - offset < 1) {
      return { frame: null, nextOffset: startOffset };
    }
    offset++; // flags

    const frameDataSize = blockSize - (trackResult.bytesRead + 2 + 1);
    if (frameDataSize <= 0 || frameDataSize > 8192) {
      return { frame: null, nextOffset: startOffset + 1 };
    }

    if (this.buffer.length - offset < frameDataSize) {
      return { frame: null, nextOffset: startOffset };
    }

    const frame = this.buffer.slice(offset, offset + frameDataSize);
    const nextOffset = offset + frameDataSize;
    return { frame, nextOffset };
  }

  /**
   * Extract Opus frame from Block element
   */
  private extractFromBlock(offset: number): {
    frame: Uint8Array | null;
    nextOffset: number;
  } {
    if (this.buffer[offset] !== 0xa1) {
      return { frame: null, nextOffset: offset };
    }

    const startOffset = offset;
    offset++;

    const sizeResult = this.readVint(offset);
    if (sizeResult.value === -1 || sizeResult.value < 1) {
      return { frame: null, nextOffset: startOffset + 1 };
    }
    offset += sizeResult.bytesRead;

    const blockSize = sizeResult.value;
    if (this.buffer.length - offset < blockSize) {
      return { frame: null, nextOffset: startOffset };
    }

    const trackResult = this.readVint(offset);
    if (trackResult.value === -1) {
      return { frame: null, nextOffset: startOffset + 1 };
    }
    offset += trackResult.bytesRead;

    if (this.buffer.length - offset < 2) {
      return { frame: null, nextOffset: startOffset };
    }
    offset += 2; // timecode

    const frameDataSize = blockSize - (trackResult.bytesRead + 2);
    if (frameDataSize <= 0 || frameDataSize > 8192) {
      return { frame: null, nextOffset: startOffset + 1 };
    }

    if (this.buffer.length - offset < frameDataSize) {
      return { frame: null, nextOffset: startOffset };
    }

    const frame = this.buffer.slice(offset, offset + frameDataSize);
    const nextOffset = offset + frameDataSize;
    return { frame, nextOffset };
  }

  private readVint(offset: number): { value: number; bytesRead: number } {
    if (offset >= this.buffer.length) {
      return { value: -1, bytesRead: 0 };
    }

    const firstByte = this.buffer[offset];
    let length = 1;
    let mask = 0x80;

    while ((firstByte & mask) === 0 && length < 8) {
      mask >>= 1;
      length++;
    }

    if (this.buffer.length - offset < length) {
      return { value: -1, bytesRead: 0 };
    }

    let value = firstByte & (mask - 1);
    for (let i = 1; i < length; i++) {
      value = (value << 8) | this.buffer[offset + i];
    }

    return { value, bytesRead: length };
  }

  reset(): void {
    this.buffer = new Uint8Array(0);
  }
}

